import React from 'react';

function MutualFunds() {
  return (
    <main className="bg-white">
      <section className="relative h-[42vh] min-h-[320px] flex items-center justify-center overflow-hidden">
        <img src="/images/medium-shot-people-working-together_23-2149271691.jpg_ws2yps.jpg" alt="Mutual Funds" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 text-center px-6">
          <h1 className="text-white text-4xl sm:text-5xl font-semibold tracking-tight">Mutual Funds</h1>
          <p className="mt-3 text-white/90 max-w-2xl mx-auto">Institutional-grade fund strategies accessible to retail and professional investors.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Why Our Funds</h2>
            <p className="mt-4 text-gray-600 leading-relaxed">Our funds combine disciplined factor tilts, risk budgeting, and cost efficiency. We prioritize downside protection while seeking consistent excess returns over relevant benchmarks.</p>
            <ul className="mt-6 space-y-3 text-gray-700 list-disc list-inside">
              <li>Clear mandates and transparent reporting</li>
              <li>Rigorous risk controls and liquidity management</li>
              <li>Experienced managers with proven track records</li>
            </ul>
          </div>
          <div>
            <img src="/images/traders-profit-279315.jpg" alt="Fund Research" className="w-full h-80 object-cover rounded-xl shadow" />
          </div>
        </div>
      </section>

      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {[
            { t: 'Equity Factor', d: 'Diversified exposure to quality, momentum, and low-vol.' },
            { t: 'Fixed Income', d: 'Laddered maturity with credit risk managed via rules.' },
            { t: 'Multi-Asset', d: 'Dynamic allocation balances growth and defense.' }
          ].map((x) => (
            <div key={x.t} className="rounded-xl bg-white p-6 shadow border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">{x.t}</h3>
              <p className="mt-2 text-gray-600 text-sm">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <img src="/images/office-1542590_640.jpg" alt="Process" className="w-full h-80 object-cover rounded-xl shadow" />
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Process & Governance</h2>
            <p className="mt-4 text-gray-600">Investment committees meet weekly; mandates reviewed quarterly. Trade execution follows best-ex standards, and we publish monthly fact sheets with holdings, performance, and risk.</p>
          </div>
        </div>
      </section>

      {/* Strategy pillars expanded */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900 text-center">Strategy Pillars</h2>
          <p className="mt-2 text-gray-600 text-center max-w-3xl mx-auto">Our approach blends evidence‑based factors with practical risk controls to deliver reliable outcomes through cycles.</p>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[{
              t:'Evidence‑based', d:'Quality, momentum and low volatility tilts supported by decades of research.'
            },{
              t:'Risk Budgeting', d:'Position sizing and guardrails to target volatility and limit drawdowns.'
            },{
              t:'Cost Discipline', d:'Low turnover, efficient vehicles, and transparent fees.'
            }].map(x => (
              <div key={x.t} className="rounded-xl p-6 bg-white shadow border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">{x.t}</h3>
                <p className="mt-2 text-gray-600 text-sm">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fund lineup */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900 text-center">Fund Lineup</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {[{
              t:'Alpha Equity Factor', img:'/images/traders-profit-354772.jpg', d:'Broad equity with quality and momentum tilts; disciplined risk controls.'
            },{
              t:'Core Bond', img:'/images/financial-crisis-544944_1280.jpg', d:'Investment‑grade bonds with balanced duration and credit risk.'
            },{
              t:'Multi‑Asset Balanced', img:'/images/office-1542590_640.jpg', d:'Dynamic mix of equities, bonds and real assets for smoother returns.'
            }].map(card => (
              <article key={card.t} className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-white">
                <img src={card.img} alt={card.t} className="w-full h-40 object-cover" />
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{card.t}</h3>
                  <p className="mt-2 text-gray-600 text-sm">{card.d}</p>
                  <div className="mt-4 flex gap-2">
                    <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700">Daily Liquidity</span>
                    <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700">UCITS‑like</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Illustrative performance snapshot (placeholder values) */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900 text-center">Illustrative Performance</h2>
          <p className="mt-2 text-gray-600 text-center max-w-3xl mx-auto">Past performance is not indicative of future results. Below shows hypothetical profile for demonstration only.</p>
          <div className="mt-8 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="text-gray-700">
                  <th className="py-3 pr-6">Fund</th>
                  <th className="py-3 pr-6">YTD</th>
                  <th className="py-3 pr-6">1Y</th>
                  <th className="py-3 pr-6">3Y (ann.)</th>
                  <th className="py-3 pr-6">Volatility</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { n:'Alpha Equity Factor', ytd:'+6.4%', y1:'+11.2%', y3:'+8.1%', vol:'12.5%' },
                  { n:'Core Bond', ytd:'+2.1%', y1:'+4.0%', y3:'+3.2%', vol:'4.3%' },
                  { n:'Multi‑Asset Balanced', ytd:'+4.0%', y1:'+7.6%', y3:'+6.0%', vol:'8.1%' }
                ].map(r => (
                  <tr key={r.n} className="text-gray-800">
                    <td className="py-3 pr-6 font-medium">{r.n}</td>
                    <td className="py-3 pr-6">{r.ytd}</td>
                    <td className="py-3 pr-6">{r.y1}</td>
                    <td className="py-3 pr-6">{r.y3}</td>
                    <td className="py-3 pr-6">{r.vol}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-gray-500">For illustration only — not actual fund results; methodology available upon request.</p>
        </div>
      </section>

      {/* How to invest */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900 text-center">How to Invest</h2>
          <div className="mt-8 grid md:grid-cols-4 gap-6">
            {[{s:'1',t:'Consultation',d:'Discuss objectives, horizon and constraints.'},{s:'2',t:'Selection',d:'Choose suitable fund(s) and share class.'},{s:'3',t:'Onboarding',d:'KYC/AML and account setup.'},{s:'4',t:'Subscription',d:'Fund funding and confirmation.'}].map(step => (
              <div key={step.s} className="relative p-6 bg-white rounded-xl shadow border border-gray-100">
                <div className="absolute -top-3 left-6 w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold">{step.s}</div>
                <h3 className="mt-2 text-lg font-semibold text-gray-900">{step.t}</h3>
                <p className="mt-1 text-gray-600 text-sm">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fees & disclosures */}
      <section className="py-14 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-white text-center">Fees & Disclosures</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {[{
              t:'Transparent Fees', d:'Management fee varies by share class; no hidden charges.'
            },{
              t:'Liquidity', d:'Subscriptions/redemptions as disclosed in offering docs.'
            },{
              t:'Risks', d:'Market, interest rate, credit and liquidity risks may lead to loss of capital.'
            }].map(x => (
              <div key={x.t} className="rounded-xl p-6 bg-white/5 border border-white/10">
                <h3 className="text-lg font-semibold text-white">{x.t}</h3>
                <p className="mt-2 text-white/80 text-sm">{x.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-white/70 text-center">Read the prospectus/fact sheet carefully before investing.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold">Speak with a fund specialist</h3>
          <p className="mt-2 text-white/80">Get share classes, minimums, and subscription details.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold">Apply now</a>
            <a href="/contact" className="px-6 py-3 rounded-md border border-white/30">Contact us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default MutualFunds;
